import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import format from 'string-template';

import {
  Backdrop,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Button, MessageBox } from 'react-chat-elements';

import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useStorage from '../../../../../hooks/useStorage';
import { mapFilesToUploadFormat, processUploadResults } from '../../../../../utils/attachments';
import { MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';
import LeadMsg, { IG_MSG_STATUS } from '../../../../Model/LeadMsg';
import Lead, { ContactProvider, LeadHistoryType } from '../../../../Model/Lead';
import InstagramUser, { accountStatus, instagramPermissions } from '../../../../../Model/InstagramUser';
import {
  sendLeadMessage,
  addLeadComment,
  returnLeadToAiSdr,
  createLeadCommentActionItems,
  updateLeadComment,
} from '../../../../utils/message';
import useToast from '../../../../hooks/useToast';
import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import AttachmentsContainer from '../../../AttachmentsContainer';
import colors from '../../../../../styles/colors';
import FirebaseContext from '../../../../../context/FirebaseContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import ActionItemsContext from '../../../../context/ActionItemsContext';
import { sendIGDM } from '../../../../utils/igMessage';
import { useInstagramDMEnabled, useInstagramAutomationEnabled } from '../../../../../utils/postHog';
import useSessionStore from '../../../../../hooks/useSessionStore';
import { useCoachConfig } from '../../../../hooks/useCoachConfig';

import ToggleButtonGroup from '../../../ToggleButtonGroup';
import MentionPopper from './MentionPopper';
import {
  StyledInput,
  ChatContainer,
  MessageListContainer,
  StyledChip,
  FilterButtonContainer,
  PaddedDiv,
  ComposerControlsRow,
  AttachmentsSlot,
  ToolbarActions,
  AddCommentButton,
  SplitSend,
  PrimarySendButton,
  CaretSendButton,
  anchorMenuStyles,
  CommentWrapper,
  EditCommentButton,
  EditingPreview,
  IGStatusContainer,
  BadgeWrapper,
  BadgeDot,
} from './styles';
import {
  mapMsgToDataSourceItem,
  onFilesSelected,
  MessageFilter,
  MessageFilterConfig,
  SendMode,
  getSendModeOptions,
  isSendModeAvailable,
  getDefaultSendMode,
  mapCommentToMsg,
} from './utils';
import texts from './texts.json';

let clearInput = () => { };

const ChatModal = ({
  lead,
  history,
  message,
  onMessageChange,
  sendMode,
  onSendModeChange,
  initialMessageFilter,
}) => {
  const {
    name, email, id: leadId, sendBlueNumber, phoneNumber: contactNo, coach: coachId,
  } = lead;

  const [isLoadingMsgs, setIsLoadingMsgs] = useState(false);
  const [msgList, setMsgList] = useState([]);
  const [messageFilter, setMessageFilter] = useState(
    initialMessageFilter || Object.keys(MessageFilterConfig)[0],
  );
  const [isSendingMsg, setIsSendingMsg] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentBody, setEditingCommentBody] = useState('');
  const [fileAttachments, setFileAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [messageCollection, setMessageCollection] = useState([]);
  const [emailCollection, setEmailCollection] = useState([]);
  const [sendAsUserName, setSendAsUserName] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [isMentionLoading, setIsMentionLoading] = useState(false);
  const [igMessageCollection, setIGMessageCollection] = useState(null);
  const [igMsgList, setIGMsgList] = useState([]);
  const [igUser, setIgUser] = useState(null);
  const [emailSubjectDraft, setEmailSubjectDraft] = useState('');
  const [sendMenuAnchor, setSendMenuAnchor] = useState(null);
  const mentionAnchorRef = useRef(null);
  const mentionLoadingInFlightRef = useRef(false);

  const { firebase: { remote } } = useContext(FirebaseContext);
  const {
    userId,
    userDoc: {
      name: userName,
    },
  } = useContext(LoggedInUserContext);
  const { internalUsers, loadInternalUsers } = useContext(ActionItemsContext);
  const { isInsideSales } = useSessionStore();
  const { coachConfig } = useCoachConfig(coachId);
  const senderEmail = coachConfig?.coachEmailAddress || '';
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const isInstagramDMEnabled = useInstagramDMEnabled();
  const isInstagramAutomationEnabled = useInstagramAutomationEnabled(coachId)
    && igUser?.accountStatus === accountStatus.AUTHORIZED
    && igUser?.grantedPermissions?.includes(instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES);
  const { uploadAttachments, deleteAttachments } = useStorage();
  const inputRef = useRef();
  const messagesEndRef = useRef(null);

  const availableMessageFilters = useMemo(() => (
    isInstagramAutomationEnabled
      ? Object.keys(MessageFilterConfig)
      : Object.keys(MessageFilterConfig).filter((filter) => filter !== MessageFilter.INSTAGRAM)
  ), [isInstagramAutomationEnabled]);

  const isIGTab = isInstagramAutomationEnabled && messageFilter === MessageFilter.INSTAGRAM;
  const isEmailTab = messageFilter === MessageFilter.EMAIL;
  const isEmailMode = sendMode === SendMode.EMAIL;
  const isCommentMode = sendMode === SendMode.COMMENT;
  // The Instagram tab has its own send path and no comments, but an edit in flight holds the
  // channel at COMMENT (the effect below deliberately leaves it alone), so the tab can be
  // reached with the comment channel still selected. The tint has to follow the same !isIGTab
  // gate as the edit preview and the channel toggle, or the composer would promise "not sent
  // to the lead" above a button whose only action is to DM the lead.
  const isCommentComposer = isCommentMode && !isIGTab;
  const sendModeOptions = useMemo(() => getSendModeOptions(messageFilter), [messageFilter]);

  useEffect(() => {
    if (!availableMessageFilters.includes(messageFilter)) {
      setMessageFilter(availableMessageFilters[0]);
    }
  }, [availableMessageFilters, messageFilter]);

  // Changing the transcript filter must not re-target a draft the rep has already started.
  // Every non-Instagram filter offers COMMENT, so a chosen comment survives the change and
  // the composer keeps its tint; only a channel the new filter cannot offer falls back.
  //
  // An edit in flight owns the composer outright: its channel is not the rep's to change
  // (the toggle is disabled) and must not be recomputed here either. Instagram offers no
  // channels at all, so without this an edit that passes through that tab would come back
  // as an outbound composer still wearing the comment-edit Save/Cancel buttons.
  useEffect(() => {
    if (editingCommentId) {
      return;
    }
    onSendModeChange((prev) => (
      isSendModeAvailable(messageFilter, prev) ? prev : getDefaultSendMode(messageFilter)
    ));
    setEmailSubjectDraft('');
  }, [messageFilter, editingCommentId, onSendModeChange]);

  // Restore draft message into the uncontrolled textarea on mount.
  useEffect(() => {
    if (message && inputRef.current) {
      inputRef.current.value = message;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const provider = isEmailMode ? ContactProvider.EMAIL : ContactProvider.SENDBLUE;
  // The channel the message really takes: the server sends a text to a lead on RCS over RCS.
  const sentProvider = !isEmailMode && lead.lastContactedMsgProvider === ContactProvider.RCS
    ? ContactProvider.RCS
    : provider;

  const commentMessages = useMemo(() => history
    .filter((item) => item.activityType === LeadHistoryType.COMMENT)
    .map((item) => mapCommentToMsg(item)),
  [history]);

  const filteredMentionUsers = useMemo(
    () => internalUsers.filter((u) => u.name.includes(mentionQuery)),
    [internalUsers, mentionQuery],
  );

  const lastInboundEmailSubject = useMemo(() => {
    for (let i = msgList.length - 1; i >= 0; i -= 1) {
      const msg = msgList[i];
      if (msg.provider === ContactProvider.EMAIL && !msg.isFromMe && !msg.isComment) {
        return msg.subject?.trim() || '';
      }
    }
    return '';
  }, [msgList]);

  const isFirstEmail = isEmailMode && !lastInboundEmailSubject;

  useEffect(() => {
    const init = async () => {
      const igUserCol = await InstagramUser.getSecondaryInstaUserByCoachId(coachId);
      if (isComponentMountedRef.current) {
        setIgUser(igUserCol.docs[0]);
      }
    };
    if (coachId) {
      init();
    }
  }, [coachId, isComponentMountedRef]);

  // Fetch messages and emails
  useEffect(() => {
    const loadMsgs = async () => {
      setMsgList([]);
      if (contactNo) {
        setIsLoadingMsgs(true);
        const msgs = await LeadMsg.getLeadMsgs(contactNo, sendBlueNumber);
        const emails = await LeadMsg.getLeadEmails(email);
        if (isComponentMountedRef.current) {
          setMessageCollection(msgs);
          setEmailCollection(emails);
          setSendAsUserName(msgs.docs?.length ? msgs.docs[0]?.sentAsUsername : '');
          setIsLoadingMsgs(false);
        }
      }
    };
    loadMsgs();
  }, [isComponentMountedRef, contactNo, email, sendBlueNumber]);

  useEffect(() => {
    const loadIGMsgs = async () => {
      const igCollection = await LeadMsg.getIGLeadMsgs(leadId);
      if (isComponentMountedRef.current) {
        setIGMessageCollection(igCollection);
      }
    };
    loadIGMsgs();
  }, [isComponentMountedRef, leadId]);

  useEffect(() => {
    if (igMessageCollection) {
      const disposer = autorun(() => {
        setIGMsgList(igMessageCollection.docs?.slice() || []);
      });
      return () => disposer();
    }
    return () => {};
  }, [igMessageCollection]);

  // Update message list when message or email collection changes
  useEffect(() => {
    if (messageCollection && emailCollection) {
      const disposer = autorun(() => {
        const msgsDocs = messageCollection?.docs?.slice() || [];
        const emailsDocs = emailCollection?.docs?.slice() || [];

        const merged = [...msgsDocs, ...emailsDocs, ...commentMessages].sort(
          (a, b) => a.sentAt - b.sentAt,
        );

        setMsgList(merged);

        requestAnimationFrame(() => {
          const container = messagesEndRef.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      });

      return () => disposer();
    }
    return () => { };
  }, [messageCollection, emailCollection, commentMessages]);

  const handleFileSelection = useCallback((event) => {
    const input = event.target;
    const selectedCount = input?.files?.length ?? 0;
    if (selectedCount > 1) {
      showToast(texts.multipleFilesError, { error: true });
      input.value = '';
      return;
    }
    const files = onFilesSelected(event);
    if (files.length) {
      setFileAttachments((prevFiles) => [...prevFiles, files[0]]);
    } else {
      showToast(format(texts.fileSizeError, { maxFileSize: MAX_FILE_UPLOAD_SIZE_MB }), { error: true });
    }
    input.value = '';
  }, [showToast]);

  const handleFileRemoved = useCallback((index) => {
    const resultAttachments = [...fileAttachments];
    resultAttachments.splice(index, 1);
    setFileAttachments(resultAttachments);
  }, [fileAttachments]);

  const onMsgSent = useCallback((msgData) => {
    lead.addItemToHistory({
      value: {
        ...msgData,
      },
      activityType: LeadHistoryType.MSG,
      createdBy: userId,
      createdByUserName: userName,
    });
  }, [lead, userId, userName]);

  const handleMessageChange = useCallback(async (e) => {
    const newValue = e.target?.value ?? '';
    onMessageChange?.(newValue);

    const match = newValue.match(/@([a-z]*)$/i);
    if (match) {
      setMentionQuery(match[1].toLowerCase());
      setMentionOpen(true);

      // Prevent multiple loading requests
      if (!mentionLoadingInFlightRef.current) {
        mentionLoadingInFlightRef.current = true;
        setIsMentionLoading(true);
        await loadInternalUsers();
        if (isComponentMountedRef.current) {
          mentionLoadingInFlightRef.current = false;
          setIsMentionLoading(false);
        }
      }
    } else {
      setMentionOpen(false);
    }
  }, [loadInternalUsers, isComponentMountedRef, onMessageChange]);

  const handleMentionSelect = useCallback((user) => {
    const atIndex = message.lastIndexOf('@');
    const newMessage = `${message.slice(0, atIndex)}@${user.name} `;
    onMessageChange?.(newMessage);
    if (inputRef.current) {
      inputRef.current.value = newMessage;
    }
    setMentionOpen(false);
    setMentionQuery('');
  }, [message, onMessageChange]);

  const performSend = useCallback(async (returnToAiSdr = false) => {
    const hasMessage = message?.trim();
    const hasAttachments = fileAttachments.length > 0;

    if (!hasMessage && !hasAttachments) {
      showToast(texts.emptyMsg, { error: true });
      return;
    }

    let emailSubject;
    if (isEmailMode) {
      if (!email) {
        showToast(format(texts.failedToSend, { error: 'No recipient email' }), { error: true });
        return;
      }
      if (isFirstEmail) {
        emailSubject = emailSubjectDraft.trim();
      } else {
        const stripped = lastInboundEmailSubject.replace(/^(re:\s*)+/i, '');
        emailSubject = `Re: ${stripped}`;
      }
      if (!emailSubject) {
        showToast(texts.subjectRequired, { error: true });
        return;
      }
    }

    setIsSendingMsg(true);
    try {
      let signedAttachmentUrls = [];

      if (!isEmailMode && hasAttachments) {
        setIsUploading(true);
        const storagePath = format(storagePaths.LEAD_CHAT_ATTACHMENTS, {
          [pathPlaceholder.LEAD_ID]: leadId,
        });

        const filesToAdd = mapFilesToUploadFormat(fileAttachments);
        const results = await uploadAttachments(filesToAdd, storagePath);
        const { uploadedRefs, failed } = await processUploadResults(results, deleteAttachments);

        if (failed) {
          throw new Error(texts.uploadError);
        }

        signedAttachmentUrls = uploadedRefs.map((res) => res.publicUrl);
        setIsUploading(false);
      }

      await sendLeadMessage({
        leadId,
        messageBody: message || '',
        msgProvider: provider,
        isLeadChat: true,
        signedAttachmentUrls,
        isInsideSales,
        agentName: userName,
        ...(isEmailMode && { emailSubject }),
      }, remote);

      showToast(isEmailMode ? texts.emailSent : texts.msgSent);
      onMessageChange?.('');
      clearInput();
      setFileAttachments([]);
      setEmailSubjectDraft('');

      onMsgSent({
        msgProvider: sentProvider,
        contactNo,
        messageBody: message || '',
        sendAsUserName,
        ...(isEmailMode && { emailSubject }),
      });

      // Hand the lead back to aiSDR when the SDR opted in on send (no-op if the lead wasn't escalated).
      // Scoped in its own try/catch: the message is already delivered by this point, so a hand-back
      // failure must not surface as a "failed to send" error.
      if (returnToAiSdr) {
        try {
          await returnLeadToAiSdr(lead, { createdBy: userId, createdByUserName: userName });
        } catch (e) {
          showToast(format(texts.failedToReturnToAiSdr, { error: e?.message }), { error: true });
        }
      }
    } catch (e) {
      showToast(format(texts.failedToSend, { error: e?.message }), { error: true });
    } finally {
      setIsSendingMsg(false);
    }
  }, [
    contactNo,
    email,
    leadId,
    lead,
    userId,
    message,
    userName,
    fileAttachments,
    onMsgSent,
    onMessageChange,
    remote,
    showToast,
    provider,
    sentProvider,
    sendAsUserName,
    isInsideSales,
    uploadAttachments,
    deleteAttachments,
    isEmailMode,
    isFirstEmail,
    emailSubjectDraft,
    lastInboundEmailSubject,
  ]);
  // Only inside-sales users can hand an escalated lead back to aiSDR.
  const canHandBackToAiSdr = isInsideSales && lead.isEscalatedFromAiSdr;

  const sendIGMsg = useCallback(async () => {
    if (!message?.trim()) {
      showToast(texts.emptyMsg, { error: true });
      return;
    }

    if (!lead.instagramHandle) {
      showToast(texts.igNoHandle, { error: true });
      return;
    }

    setIsSendingMsg(true);
    try {
      await sendIGDM({
        leadId,
        coachId,
        igHandle: lead.instagramHandle,
        body: message,
        userId,
        userName,
        leadName: name,
      });
      showToast(texts.igDMSent);
      onMessageChange?.('');
      if (inputRef.current) inputRef.current.value = '';
      clearInput();
    } catch (e) {
      showToast(format(texts.igDMFailed, { error: e?.message }), { error: true });
    } finally {
      setIsSendingMsg(false);
    }
  }, [lead, leadId, coachId, message, userId, userName, onMessageChange, showToast, name]);

  const addComment = useCallback(async () => {
    const hasMessage = message?.trim();

    if (!hasMessage) {
      showToast(texts.emptyComment, { error: true });
      return;
    }

    try {
      setIsSendingMsg(true);

      await addLeadComment({
        leadId,
        coachId,
        message,
        createdBy: userId,
        createdByUserName: userName,
      });

      const mentionedUserIds = internalUsers
        .filter((u) => message.includes(`@${u.name}`))
        .map((u) => u.id);

      await createLeadCommentActionItems({
        mentionedUserIds,
        leadId,
        leadName: name,
        triggeredBy: userId,
        triggerUserName: userName,
        message,
      });

      if (isComponentMountedRef.current) {
        showToast(texts.commentAdded);
        onMessageChange?.('');
        clearInput();
        // A comment carries no attachments, and the attachments slot is hidden while the
        // comment channel is selected. Clearing here — the way performSend does — stops a file
        // picked for the outbound draft this comment replaced from surviving invisibly and
        // reappearing on the next outbound channel, attached to a draft that no longer exists.
        setFileAttachments([]);
      }
    } catch (error) {
      showToast(error.message || texts.failedToAddComment, { error: true });
    } finally {
      setIsSendingMsg(false);
    }
  }, [
    message,
    leadId,
    coachId,
    name,
    userId,
    userName,
    internalUsers,
    onMessageChange,
    showToast,
    isComponentMountedRef,
  ]);

  const startEditComment = useCallback((comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentBody(comment.body);
    onSendModeChange(SendMode.COMMENT);
    onMessageChange?.(comment.body);
    if (inputRef.current) {
      inputRef.current.value = comment.body;
      inputRef.current.focus();
    }
  }, [onMessageChange, onSendModeChange]);

  const cancelEdit = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentBody('');
    onSendModeChange(getDefaultSendMode(messageFilter));
    onMessageChange?.('');
    clearInput();
  }, [messageFilter, onMessageChange, onSendModeChange]);

  const saveEditedComment = useCallback(async () => {
    if (!message?.trim()) {
      showToast(texts.emptyComment, { error: true });
      return;
    }
    setIsSendingMsg(true);
    try {
      const isLastMessage = msgList[msgList.length - 1]?.id === editingCommentId;
      await updateLeadComment({ commentId: editingCommentId, message, leadId: isLastMessage ? leadId : null });
      if (isComponentMountedRef.current) {
        showToast(texts.commentUpdated);
        setMsgList((prev) => prev.map((msg) => (
          msg.id === editingCommentId ? { ...msg, body: message } : msg
        )));
        onMessageChange?.('');
        if (inputRef.current) inputRef.current.value = '';
        clearInput();
        setEditingCommentId(null);
        setEditingCommentBody('');
        onSendModeChange(getDefaultSendMode(messageFilter));
      }
    } catch (error) {
      showToast(texts.failedToUpdateComment, { error: true });
    } finally {
      setIsSendingMsg(false);
    }
  }, [
    message,
    editingCommentId,
    msgList,
    leadId,
    messageFilter,
    showToast,
    isComponentMountedRef,
    onMessageChange,
    onSendModeChange,
  ]);

  const filteredMsgList = useMemo(() => {
    if (isIGTab) {
      return igMsgList;
    }
    const nonIGMsgs = msgList.filter(
      (msg) => msg.provider !== ContactProvider.INSTAGRAM,
    );
    // Comments carry no provider, so they belong to no single channel. The Text tab already
    // shows them; the Email tab excluding them meant a comment added there vanished from the
    // view that accepted it. Keep all three non-Instagram tabs consistent.
    if (isEmailTab) {
      return nonIGMsgs.filter((msg) => msg.provider === ContactProvider.EMAIL || msg.isComment);
    }
    if (messageFilter === MessageFilter.TEXT_ONLY) {
      return nonIGMsgs.filter((msg) => msg.provider !== ContactProvider.EMAIL);
    }
    return nonIGMsgs;
  }, [isIGTab, isEmailTab, messageFilter, msgList, igMsgList]);

  const hasFilteredMsgs = !isLoadingMsgs && filteredMsgList.length > 0;

  const renderInputChips = () => {
    if (isCommentMode) {
      return <StyledChip label={texts.commentChip} />;
    }
    if (isEmailMode) {
      return (
        <>
          {!!senderEmail && <StyledChip label={`${texts.sendFrom}: ${senderEmail}`} />}
        </>
      );
    }
    if (isIGTab) return null;
    return (
      <>
        {!!sentProvider && <StyledChip label={`${texts.provider}: ${sentProvider}`} />}
      </>
    );
  };

  const renderRightButtons = () => {
    if (isIGTab) {
      return (
        <PrimaryButton onClick={sendIGMsg} size="small">
          {texts.sendIGDM}
        </PrimaryButton>
      );
    }
    if (editingCommentId) {
      return (
        <>
          <AddCommentButton onClick={saveEditedComment} size="small">
            {texts.saveComment}
          </AddCommentButton>
          <PrimaryButton onClick={cancelEdit} size="small">{texts.cancelEdit}</PrimaryButton>
        </>
      );
    }
    // The composer's channel decides the action, so there is only ever one primary button
    // and its label states where the text is going. Nothing about commenting is nested in
    // a menu, and there is no second send-shaped button beside it to mis-click.
    if (isCommentMode) {
      return (
        <AddCommentButton onClick={addComment} size="small" disabled={isSendingMsg}>
          {texts.comment}
        </AddCommentButton>
      );
    }
    if (!canHandBackToAiSdr) {
      return (
        <PrimaryButton onClick={() => performSend(false)} size="small" disabled={isSendingMsg}>
          {texts.send}
        </PrimaryButton>
      );
    }
    return (
      <>
        <SplitSend>
          <PrimarySendButton size="small" onClick={() => performSend(false)} disabled={isSendingMsg}>
            {texts.send}
          </PrimarySendButton>
          <CaretSendButton size="small" onClick={(e) => setSendMenuAnchor(e.currentTarget)}>
            <ArrowDropDownIcon fontSize="small" />
          </CaretSendButton>
        </SplitSend>
        <Menu
          anchorEl={sendMenuAnchor}
          open={Boolean(sendMenuAnchor)}
          onClose={() => setSendMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => { setSendMenuAnchor(null); performSend(true); }}
            sx={anchorMenuStyles}
          >
            <SmartToyOutlinedIcon fontSize="small" />
            {texts.sendAndHandback}
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <ChatContainer>
      <FilterButtonContainer>
        {availableMessageFilters.map((filter) => {
          const showBadge = filter === MessageFilter.INSTAGRAM
            && messageFilter !== MessageFilter.INSTAGRAM
            && igMsgList.length > 0;

          return (
            <BadgeWrapper key={filter}>
              <Button
                onClick={() => setMessageFilter(filter)}
                text={MessageFilterConfig[filter].label}
                color={messageFilter === filter ? colors.base.alpha : colors.base.beta}
                backgroundColor={messageFilter === filter ? colors.shades.secondary8 : colors.shades.gamma1}
              />
              {showBadge && <BadgeDot />}
            </BadgeWrapper>
          );
        })}
      </FilterButtonContainer>
      <MessageListContainer ref={messagesEndRef}>
        {hasFilteredMsgs && filteredMsgList.map((msg) => {
          const renderedMsg = msg.isComment && msg.createdBy === userId ? (
            <CommentWrapper key={msg.id}>
              <MessageBox {...mapMsgToDataSourceItem(msg, name)} />
              <EditCommentButton onClick={() => startEditComment(msg)} size="small" title="Edit comment">
                <EditOutlinedIcon fontSize="inherit" />
              </EditCommentButton>
            </CommentWrapper>
          ) : (
            <MessageBox key={msg.id} {...mapMsgToDataSourceItem(msg, name)} />
          );

          const showIGStatus = isIGTab && msg.isFromMe && msg.provider === ContactProvider.INSTAGRAM;
          if (!showIGStatus) return renderedMsg;

          const { igMsgStatus } = msg;
          const isPending = igMsgStatus === IG_MSG_STATUS.QUEUED || igMsgStatus === IG_MSG_STATUS.PROCESSING;
          const isError = igMsgStatus === IG_MSG_STATUS.ERROR;
          // Without this the message renders undecorated, which reads as sent — and a setter would
          // open the thread believing the lead received a DM that was never delivered.
          const isCancelled = igMsgStatus === IG_MSG_STATUS.CANCELLED;

          return (
            <React.Fragment key={msg.id}>
              {renderedMsg}
              {isPending && (
                <IGStatusContainer>
                  <span>{texts.igPending}</span>
                </IGStatusContainer>
              )}
              {isCancelled && (
                <IGStatusContainer>
                  <span>{msg.automatedMsgCancellationReason}</span>
                </IGStatusContainer>
              )}
              {isError && (
                <IGStatusContainer $isError>
                  <span>{msg.errorMessage}</span>
                  <Button text={texts.igRetry} onClick={() => msg.retryIGMsg()} />
                </IGStatusContainer>
              )}
            </React.Fragment>
          );
        })}
        {!hasFilteredMsgs && !isLoadingMsgs && texts.noMsgs}
      </MessageListContainer>
      {(!isIGTab || isInstagramDMEnabled) && (
        <PaddedDiv ref={mentionAnchorRef} $isComment={isCommentComposer}>
          {!!editingCommentId && !isIGTab && (
            <EditingPreview>
              <span>
                <strong>{texts.editingComment}</strong>
                {editingCommentBody}
              </span>
            </EditingPreview>
          )}
          {!isIGTab && (
            <ComposerControlsRow>
              {sendModeOptions.length > 0 && (
                <ToggleButtonGroup
                  value={sendMode}
                  options={sendModeOptions}
                  onChange={onSendModeChange}
                  disabled={!!editingCommentId}
                />
              )}
              {!isEmailMode && !isCommentMode && !editingCommentId && (
                <AttachmentsSlot>
                  <AttachmentsContainer
                    fileAttachments={fileAttachments}
                    onFilesSelected={handleFileSelection}
                    onFileRemoved={handleFileRemoved}
                    isUploading={isUploading}
                    disabled={!!fileAttachments.length}
                  />
                </AttachmentsSlot>
              )}
              {isEmailMode && isFirstEmail && (
                <StyledInput
                  placeholder={texts.subjectPlaceholder}
                  maxlength={998}
                  multiline={false}
                  value={emailSubjectDraft}
                  onChange={(e) => setEmailSubjectDraft(e.target.value)}
                />
              )}
              {renderInputChips()}
            </ComposerControlsRow>
          )}
          <StyledInput
            $isComment={isCommentComposer}
            placeholder={texts.typeHere}
            multiline
            autofocus
            value={message}
            onChange={handleMessageChange}
            clear={(clear) => { clearInput = clear; }}
            referance={inputRef}
            rightButtons={(
              <ToolbarActions>
                {renderRightButtons()}
              </ToolbarActions>
            )}
          />
          {!isIGTab && (
            <MentionPopper
              open={mentionOpen}
              anchorEl={mentionAnchorRef.current}
              isLoading={isMentionLoading}
              users={filteredMentionUsers}
              onMentionSelect={handleMentionSelect}
            />
          )}
        </PaddedDiv>
      )}
      <Backdrop open={isLoadingMsgs || isSendingMsg} sx={{ position: 'absolute' }}>
        <CircularProgress />
      </Backdrop>
    </ChatContainer>
  );
};

ChatModal.propTypes = {
  lead: PropTypes.instanceOf(Lead).isRequired,
  history: PropTypes.arrayOf(PropTypes.object).isRequired,
  message: PropTypes.string,
  onMessageChange: PropTypes.func,
  sendMode: PropTypes.string,
  onSendModeChange: PropTypes.func,
  initialMessageFilter: PropTypes.string,
};

ChatModal.defaultProps = {
  message: '',
  onMessageChange: null,
  sendMode: SendMode.TEXT,
  onSendModeChange: () => {},
  initialMessageFilter: null,
};

export default compose(
  observer,
)(ChatModal);
